/**
 * Redux state for Select Entities modal (users/groups picker).
 * Caches fetched users and groups so that opening "Select owners" and "Select members"
 * reuses the same data and avoids re-fetches.
 */

const actionTypes = {
  SET_USERS: 'selectEntities/SET_USERS',
  SET_GROUPS: 'selectEntities/SET_GROUPS',
  SET_USERS_LOADING: 'selectEntities/SET_USERS_LOADING',
  SET_GROUPS_LOADING: 'selectEntities/SET_GROUPS_LOADING',
  SET_ERROR: 'selectEntities/SET_ERROR',
  CLEAR_CACHE: 'selectEntities/CLEAR_CACHE',
  CLEAR_USERS_KEY: 'selectEntities/CLEAR_USERS_KEY',
}

const initialState = {
  /** Key: cacheKey (customerId-organizationId-integrationType), value: { users: [], fetchedAt: number } */
  usersCache: {},
  /** Key: customerId, value: { groups: [], fetchedAt: number } */
  groupsCache: {},
  usersLoading: false,
  groupsLoading: false,
  error: null,
}

export const reducer = (state = initialState, action) => {
  const safeState = state && typeof state === 'object' && state !== null ? state : initialState
  switch (action.type) {
    case actionTypes.SET_USERS: {
      const { cacheKey, users } = action.payload
      return {
        ...safeState,
        usersCache: {
          ...safeState.usersCache,
          [cacheKey]: {
            users: users || [],
            fetchedAt: Date.now(),
          },
        },
        usersLoading: false,
        error: null,
      }
    }
    case actionTypes.SET_GROUPS: {
      const { cacheKey, groups } = action.payload
      const key = String(cacheKey)
      return {
        ...safeState,
        groupsCache: {
          ...safeState.groupsCache,
          [key]: {
            groups: groups || [],
            fetchedAt: Date.now(),
          },
        },
        groupsLoading: false,
        error: null,
      }
    }
    case actionTypes.SET_USERS_LOADING:
      return { ...safeState, usersLoading: action.payload }
    case actionTypes.SET_GROUPS_LOADING:
      return { ...safeState, groupsLoading: action.payload }
    case actionTypes.SET_ERROR:
      return { ...safeState, usersLoading: false, groupsLoading: false, error: action.payload }
    case actionTypes.CLEAR_CACHE:
      return { ...initialState }
    case actionTypes.CLEAR_USERS_KEY: {
      const cacheKey = action.payload
      if (!cacheKey) return safeState
      const nextUsersCache = { ...safeState.usersCache }
      delete nextUsersCache[cacheKey]
      return {
        ...safeState,
        usersCache: nextUsersCache,
      }
    }
    default:
      return safeState
  }
}

export const actions = {
  setUsers: (cacheKey, users) => ({
    type: actionTypes.SET_USERS,
    payload: { cacheKey, users },
  }),
  setGroups: (cacheKey, groups) => ({
    type: actionTypes.SET_GROUPS,
    payload: { cacheKey, groups },
  }),
  setUsersLoading: (loading) => ({
    type: actionTypes.SET_USERS_LOADING,
    payload: loading,
  }),
  setGroupsLoading: (loading) => ({
    type: actionTypes.SET_GROUPS_LOADING,
    payload: loading,
  }),
  setError: (error) => ({
    type: actionTypes.SET_ERROR,
    payload: error,
  }),
  clearCache: () => ({ type: actionTypes.CLEAR_CACHE }),
  clearUsersKey: (cacheKey) => ({ type: actionTypes.CLEAR_USERS_KEY, payload: cacheKey }),
}

/**
 * Build cache key for users list (same list used for owners and members).
 * `license` / `requiredModules` change the API response shape; include them so caches do not collide.
 * Omitted options default to unlicensed full list (matches SelectEntitiesModal / `license: false`, no modules filter).
 */
export const getUsersCacheKey = (customerId, organizationId, integrationType, options = {}) => {
  const { license = false, requiredModules } = options
  // Reject null/undefined; do not use a placeholder org id (e.g. 0) — callers must wait until org is known.
  if (customerId == null || organizationId == null || !integrationType) return null
  let key = `${customerId}-${organizationId}-${integrationType}`
  if (license) {
    key += '-l1'
  }
  if (requiredModules != null && requiredModules !== '') {
    key += `-m${String(requiredModules)}`
  }
  return key
}

/**
 * Build cache key for groups list. When allowedGroupTypes is provided (e.g. [SECURITY] for Entra member picker),
 * cache is keyed by customerId + types so different use cases don't share the same list.
 */
export const getGroupsCacheKey = (customerId, allowedGroupTypes) => {
  if (customerId == null) return null
  if (!allowedGroupTypes || allowedGroupTypes.length === 0) {
    return String(customerId)
  }
  return `${customerId}_${[...allowedGroupTypes].sort().join(',')}`
}
